import React, { useState, useMemo, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { getAllBlogPosts } from '../../data/blogDataLoader';
import './BlogList.css';
import { CTA } from '../../components';

const PAGE_URL = 'https://www.snageasy.in/blog';
const PAGE_TITLE = 'Home Inspection Blog | Expert Guides, Tips & Case Studies | SnagEasy';
const PAGE_DESC = 'Read expert home inspection guides, real case studies, RERA tips, and property insights from SnagEasy — India\'s trusted home inspection service in Mumbai, Pune, Bangalore, Chennai & Lonavala.';

const POSTS_PER_PAGE = 9;

const BlogList = () => {
  const [allBlogPosts, setAllBlogPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadBlogPosts = async () => {
      try {
        const posts = await getAllBlogPosts();
        setAllBlogPosts(posts);
        // Mark blog as loaded for pre-rendering
        document.body.setAttribute('data-blog-loaded', 'true');
      } catch (error) {
        console.error('Error loading blog posts:', error);
        // Fallback to empty array or handle error
        setAllBlogPosts([]);
        document.body.setAttribute('data-blog-loaded', 'true');
      } finally {
        setIsLoading(false);
      }
    };
    loadBlogPosts();
  }, []);
  
  // State management
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [sortBy, setSortBy] = useState('date-desc'); // date-desc, date-asc, title-asc
  const [currentPage, setCurrentPage] = useState(1);

  // Get unique categories and tags
  const categories = useMemo(() => {
    if (!allBlogPosts || allBlogPosts.length === 0) return [];
    const cats = [...new Set(allBlogPosts.map(post => post.category))];
    return cats.filter(Boolean).sort();
  }, [allBlogPosts]);

  const tags = useMemo(() => {
    if (!allBlogPosts || allBlogPosts.length === 0) return [];
    const allTags = allBlogPosts.flatMap(post => post.tags || []);
    const uniqueTags = [...new Set(allTags)];
    return uniqueTags.filter(Boolean).sort();
  }, [allBlogPosts]);

  // Filter and sort posts
  const filteredAndSortedPosts = useMemo(() => {
    if (!allBlogPosts || allBlogPosts.length === 0) return [];
    let filtered = [...allBlogPosts];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(post => 
        post.title.toLowerCase().includes(query) ||
        post.excerpt.toLowerCase().includes(query) ||
        (post.tags && post.tags.some(tag => tag.toLowerCase().includes(query)))
      );
    }

    // Category filter
    if (selectedCategory) {
      filtered = filtered.filter(post => post.category === selectedCategory);
    }

    // Tag filter
    if (selectedTag) {
      filtered = filtered.filter(post => 
        post.tags && post.tags.includes(selectedTag)
      );
    }

    // Sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'date-desc':
          return new Date(b.publishDate) - new Date(a.publishDate);
        case 'date-asc':
          return new Date(a.publishDate) - new Date(b.publishDate);
        case 'title-asc':
          return a.title.localeCompare(b.title);
        default:
          return 0;
      }
    });

    return filtered;
  }, [allBlogPosts, searchQuery, selectedCategory, selectedTag, sortBy]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedPosts.length / POSTS_PER_PAGE);
  const startIndex = (currentPage - 1) * POSTS_PER_PAGE;
  const paginatedPosts = filteredAndSortedPosts.slice(startIndex, startIndex + POSTS_PER_PAGE);

  // Reset to page 1 when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedCategory, selectedTag, sortBy]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCategory('');
    setSelectedTag('');
    setSortBy('date-desc');
  };

  return (
    <>
    <Helmet>
      <title>{PAGE_TITLE}</title>
      <meta name="description" content={PAGE_DESC} />
      <meta name="robots" content="index, follow, max-image-preview:large" />
      <meta property="og:title" content={PAGE_TITLE} />
      <meta property="og:description" content={PAGE_DESC} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={PAGE_URL} />
      <meta property="og:site_name" content="SnagEasy" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={PAGE_TITLE} />
      <meta name="twitter:description" content={PAGE_DESC} />
      <link rel="canonical" href={PAGE_URL} />
    </Helmet>
    <div className="blog-list-page">
      {/* Hero Section */}
      <section className="blog-list-hero">
        <div className="blog-list-hero-container">
          <h1 className="blog-list-title">Knowledge Center</h1>
          <p className="blog-list-subtitle">
            Expert insights, tips, and guides for home inspection and property management
          </p>
        </div>
      </section>

      {/* Filters and Search Section */}
      <section className="blog-filters-section">
        <div className="blog-filters-container">
          {/* Search Bar */}
          <div className="blog-search-wrapper">
            <div className="blog-search-box">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
              <input
                type="text"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="blog-search-input"
              />
              {searchQuery && (
                <button 
                  onClick={() => setSearchQuery('')}
                  className="blog-search-clear"
                  aria-label="Clear search"
                >
                  ×
                </button>
              )}
            </div>
          </div>

          {/* Filters Row */}
          <div className="blog-filters-row">
            {/* Category Filter */}
            <div className="blog-filter-group">
              <label className="blog-filter-label">Category:</label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="blog-filter-select"
              >
                <option value="">All Categories</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            {/* Tag Filter */}
            <div className="blog-filter-group">
              <label className="blog-filter-label">Tag:</label>
              <select
                value={selectedTag}
                onChange={(e) => setSelectedTag(e.target.value)}
                className="blog-filter-select"
              >
                <option value="">All Tags</option>
                {tags.map(tag => (
                  <option key={tag} value={tag}>{tag}</option>
                ))}
              </select>
            </div>

            {/* Sort Filter */}
            <div className="blog-filter-group">
              <label className="blog-filter-label">Sort:</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="blog-filter-select"
              >
                <option value="date-desc">Newest First</option>
                <option value="date-asc">Oldest First</option>
                <option value="title-asc">Title A-Z</option>
              </select>
            </div>

            {/* Clear Filters */}
            {(selectedCategory || selectedTag || searchQuery) && (
              <button onClick={clearFilters} className="blog-clear-filters-btn">
                Clear Filters
              </button>
            )}
          </div>

          {/* Results Count */}
          <div className="blog-results-count">
            Showing {paginatedPosts.length > 0 ? startIndex + 1 : 0} - {Math.min(startIndex + POSTS_PER_PAGE, filteredAndSortedPosts.length)} of {filteredAndSortedPosts.length} articles
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="blog-posts-section">
        <div className="blog-posts-container">
          {filteredAndSortedPosts.length > 0 ? (
            <>
              {/*
                Every filtered/sorted post renders here, not just the current
                page's slice. Off-page cards are hidden with CSS
                (.blog-post-card--offpage) rather than left out of the DOM, so
                every post keeps a real, crawlable <a href> on this page
                regardless of pagination state — react-snap only prerenders
                page 1's state, and a page with zero inbound internal links is
                effectively invisible to Google even if it's in the sitemap.
                Clicking a page number just toggles which cards are visible.
              */}
              <div className="blog-posts-grid">
                {filteredAndSortedPosts.map((post, index) => {
                  const isOnCurrentPage = index >= startIndex && index < startIndex + POSTS_PER_PAGE;
                  return (
                  <article
                    key={post.id}
                    className={`blog-post-card${isOnCurrentPage ? '' : ' blog-post-card--offpage'}`}
                    aria-hidden={isOnCurrentPage ? undefined : 'true'}
                  >
                    <div className="blog-post-image">
                      <img src={post.featuredImage} alt={post.title} />
                      <div className="blog-post-category">{post.category}</div>
                    </div>
                    
                    <div className="blog-post-content">
                      <div className="blog-post-meta">
                        <span className="blog-post-date">{formatDate(post.publishDate)}</span>
                        <span className="blog-post-read-time">{post.readTime}</span>
                      </div>
                      
                      <h2 className="blog-post-title">
                        <a href={`/blog/${post.slug}`}>{post.title}</a>
                      </h2>
                      
                      <p className="blog-post-excerpt">{post.excerpt}</p>

                      {/* Tags */}
                      {post.tags && post.tags.length > 0 && (
                        <div className="blog-post-tags">
                          {post.tags.slice(0, 3).map(tag => (
                            <span key={tag} className="blog-post-tag">{tag}</span>
                          ))}
                        </div>
                      )}
                      
                      <div className="blog-post-footer">
                        <div className="blog-post-author">
                          <div className="author-avatar">
                            <span>{post.author.charAt(0)}</span>
                          </div>
                          <span className="author-name">{post.author}</span>
                        </div>
                        
                        <a href={`/blog/${post.slug}`} className="read-more-link">
                          Read More
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M5 12h14M12 5l7 7-7 7"/>
                          </svg>
                        </a>
                      </div>
                    </div>
                  </article>
                  );
                })}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="blog-pagination">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="blog-pagination-btn"
                  >
                    ← Previous
                  </button>
                  
                  <div className="blog-pagination-numbers">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
                      // Show first page, last page, current page, and pages around current
                      if (
                        page === 1 ||
                        page === totalPages ||
                        (page >= currentPage - 1 && page <= currentPage + 1)
                      ) {
                        return (
                          <button
                            key={page}
                            onClick={() => handlePageChange(page)}
                            className={`blog-pagination-number ${currentPage === page ? 'active' : ''}`}
                          >
                            {page}
                          </button>
                        );
                      } else if (page === currentPage - 2 || page === currentPage + 2) {
                        return <span key={page} className="blog-pagination-ellipsis">...</span>;
                      }
                      return null;
                    })}
                  </div>
                  
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="blog-pagination-btn"
                  >
                    Next →
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="blog-no-results">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
              <h3>No articles found</h3>
              <p>Try adjusting your search or filters to find what you're looking for.</p>
              <button onClick={clearFilters} className="blog-clear-filters-btn">
                Clear All Filters
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Newsletter Signup */}
      <section className="newsletter-section">
        <div className="newsletter-container">
          <div className="newsletter-content">
            <h2>Stay Updated</h2>
            <p>Get the latest home inspection tips and industry insights delivered to your inbox.</p>
            <div className="newsletter-form">
              <input type="email" placeholder="Enter your email address" />
              <button type="submit">Subscribe</button>
            </div>
          </div>
        </div>
      </section>

      <CTA />
    </div>
    </>
  );
};

export default BlogList;
